import { io } from 'socket.io-client'

class SocketService {
  constructor() {
    this.socket = null
    this.isConnected = false
  }

  connect(token) {
    if (this.socket?.connected) {
      return this.socket
    }

    const serverUrl = import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:5000'
    
    this.socket = io(serverUrl, {
      auth: {
        token
      },
      transports: ['websocket', 'polling'],
      upgrade: true,
      rememberUpgrade: true,
    })

    this.socket.on('connect', () => {
      console.log('Socket connected:', this.socket.id)
      this.isConnected = true
    })

    this.socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason)
      this.isConnected = false
    })

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error)
      this.isConnected = false
    })

    return this.socket
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
      this.isConnected = false
    }
  }

  // Chat events
  joinChat(chatId) {
    if (this.socket?.connected) {
      this.socket.emit('joinChat', chatId)
    }
  }

  leaveChat(chatId) {
    if (this.socket?.connected) {
      this.socket.emit('leaveChat', chatId)
    }
  }

  sendMessage(messageData) {
    if (this.socket?.connected) {
      this.socket.emit('sendMessage', messageData)
    }
  }

  // Typing events
  startTyping(chatId) {
    if (this.socket?.connected) {
      this.socket.emit('typing', { chatId, isTyping: true })
    }
  }

  stopTyping(chatId) {
    if (this.socket?.connected) {
      this.socket.emit('typing', { chatId, isTyping: false })
    }
  }

  // Reaction events
  toggleReaction(messageId, emoji) {
    if (this.socket?.connected) {
      this.socket.emit('toggleReaction', { messageId, emoji })
    }
  }

  // Read receipt events
  markMessageRead(messageId, chatId) {
    if (this.socket?.connected) {
      this.socket.emit('markMessageRead', { messageId, chatId })
    }
  }

  // Status events
  updateStatus(status) {
    if (this.socket?.connected) {
      this.socket.emit('updateStatus', status)
    }
  }

  // Chat creation events
  notifyChatCreated(chatData) {
    if (this.socket?.connected) {
      this.socket.emit('chatCreated', chatData)
    }
  }

  // Event listeners
  onNewMessage(callback) {
    if (this.socket) {
      this.socket.on('newMessage', callback)
    }
  }

  onUserTyping(callback) {
    if (this.socket) {
      this.socket.on('userTyping', callback)
    }
  }

  onReactionUpdate(callback) {
    if (this.socket) {
      this.socket.on('reactionUpdate', callback)
    }
  }

  onMessageRead(callback) {
    if (this.socket) {
      this.socket.on('messageRead', callback)
    }
  }

  onUserOnline(callback) {
    if (this.socket) {
      this.socket.on('userOnline', callback)
    }
  }

  onUserOffline(callback) {
    if (this.socket) {
      this.socket.on('userOffline', callback)
    }
  }

  onStatusUpdate(callback) {
    if (this.socket) {
      this.socket.on('statusUpdate', callback)
    }
  }

  onNewChat(callback) {
    if (this.socket) {
      this.socket.on('newChat', callback)
    }
  }

  onError(callback) {
    if (this.socket) {
      this.socket.on('error', callback)
    }
  }

  // Remove listeners
  removeAllListeners() {
    if (this.socket) {
      this.socket.removeAllListeners()
    }
  }

  removeListener(event) {
    if (this.socket) {
      this.socket.off(event)
    }
  }

  // Get socket instance
  getSocket() {
    return this.socket
  }

  // Check connection status
  isSocketConnected() {
    return this.socket?.connected || false
  }
}

// Create singleton instance
const socketService = new SocketService()

export default socketService